import React, { useMemo } from "react";
import CostBreakdownModal, {
  CostBreakdownSubtree,
  CostBreakdownLoading,
  CostBreakdownEmpty,
} from "components/financial/CostBreakdownModal";
import {
  useGetAiUsageLogStatsQuery,
  useGetAiUsageLogNetworksQuery,
  useGetAiUsageLogNetworkAgentsQuery,
} from "apis/aiUsageLog";
import { useGetProductTagsQuery } from "apis/productTag";
import ProductInput from "components/financial/ProductInput";
import PlatformMonthlyPricing from "components/financial/PlatformMonthlyPricing";

const num = (n) => (n ?? 0).toLocaleString("en-AU");

const FEATURE_LEVELS = [
  {
    header: "Feature",
    columns: [
      { key: "calls", header: "Calls", align: "right" },
      { key: "tokens", header: "Tokens", align: "right" },
    ],
  },
];

const FEATURE_LEVELS_WITH_IMAGES = [
  {
    header: "Feature",
    columns: [
      { key: "calls", header: "Calls", align: "right" },
      { key: "tokens", header: "Tokens", align: "right" },
      { key: "images", header: "Images", align: "right" },
    ],
  },
];

const LEVELS = [
  {
    header: "Network",
    columns: [
      { key: "calls", header: "Calls", align: "right" },
      { key: "tokens", header: "Tokens", align: "right" },
    ],
  },
  {
    header: "Agent",
    columns: [
      { key: "calls", header: "Calls", align: "right" },
      { key: "tokens", header: "Tokens", align: "right" },
    ],
  },
  {
    header: "Property",
    columns: [
      { key: "calls", header: "Calls", align: "right" },
      { key: "tokens", header: "Tokens", align: "right" },
    ],
  },
  {
    header: "Type",
    columns: [
      { key: "calls", header: "Calls", align: "right" },
      { key: "tokens", header: "Tokens", align: "right" },
    ],
  },
];

/** Types within one property, derived from that property's log sample. */
const typeNodes = (property) => {
  const logs = property.logs ?? [];
  const types = new Map();
  for (const log of logs) {
    const key = log.feature || log.operation || "unknown";
    if (!types.has(key)) {
      types.set(key, { calls: 0, tokens: 0, models: new Set() });
    }
    const t = types.get(key);
    t.calls += 1;
    t.tokens += log.totalTokens ?? 0;
    if (log.model) t.models.add(log.model);
  }
  return [...types.entries()]
    .map(([type, v]) => ({
      id: `${property.propertyId || "none"}-${type}`,
      label: type,
      sublabel: [...v.models].join(", ") || null,
      values: {
        calls: num(v.calls),
        tokens: num(v.tokens),
      },
    }))
    .sort((a, b) => b.calls - a.calls);
};

/** Agents for one network — fetched on expand. */
const NetworkAgents = ({ node, depth, provider, aiParams }) => {
  const { data: agents, isPending, isError } =
    useGetAiUsageLogNetworkAgentsQuery(node.networkId, {
      ...aiParams,
      provider,
    });

  const nodes = useMemo(
    () =>
      [...(agents || [])]
        .sort((a, b) => (b.totalCalls ?? 0) - (a.totalCalls ?? 0))
        .map((agent) => ({
          id: agent._id || agent.userId,
          label: agent.name || agent.agentName || "Unknown agent",
          sublabel: agent.email || null,
          values: {
            calls: num(agent.totalCalls),
            tokens: num(agent.totalTokens),
          },
          children: [...(agent.properties ?? [])]
            .sort((a, b) => (b.callCount ?? 0) - (a.callCount ?? 0))
            .map((property, i) => ({
              id: `${agent._id || agent.userId}-${property.propertyId || "none"}-${i}`,
              label: property.address || "No property",
              muted: !property.propertyId,
              badge: (property.logs ?? []).length >= 50 ? "sample" : null,
              values: {
                calls: num(property.callCount),
                tokens: num(property.totalTokens),
              },
              children: typeNodes(property),
            })),
        })),
    [agents],
  );

  if (isPending) return <CostBreakdownLoading text="Loading agents…" />;
  if (isError) return <CostBreakdownEmpty text="Agent data could not be loaded." />;
  if (!nodes.length) {
    return <CostBreakdownEmpty text="No agent activity for this provider in the period." />;
  }
  return <CostBreakdownSubtree nodes={nodes} depth={depth} levels={LEVELS} />;
};

const NON_CUSTOMER_BUCKETS = ["unattributed", "unknown", "unlinked"];

const AiCostBreakdown = ({
  isOpen,
  onClose,
  provider,
  label,
  aiParams,
  monthlyPrices = {},
  dateValue = {},
  onSetMonthlyPrice,
  onUpsertProduct,
}) => {
  // Lazy-load: only fetch when the modal is open.
  const { data: statsData } = useGetAiUsageLogStatsQuery(
    { ...aiParams, provider },
    isOpen && !!provider,
  );
  const { data: networks } = useGetAiUsageLogNetworksQuery(
    aiParams,
    isOpen && !!provider,
  );
  const { data: productTags } = useGetProductTagsQuery("aiFeature", isOpen);

  const featureCounts = statsData?.featureCounts;

  const networkRows = useMemo(
    () =>
      (networks || [])
        .map((network) => ({
          network,
          stats: network.providerBreakdown?.[provider],
        }))
        .filter((r) => (r.stats?.totalCalls ?? 0) > 0)
        .sort((a, b) => (b.stats.totalCalls ?? 0) - (a.stats.totalCalls ?? 0)),
    [networks, provider],
  );

  const totalCalls = networkRows.reduce((s, r) => s + (r.stats.totalCalls ?? 0), 0);
  const totalTokens = networkRows.reduce(
    (s, r) => s + (r.stats.totalTokens ?? 0),
    0,
  );

  const nodes = networkRows.map(({ network, stats }) => ({
    id: network._id,
    networkId: network._id,
    label: network.networkName,
    muted: network.isAdmin || NON_CUSTOMER_BUCKETS.includes(network._id),
    lazy: true,
    values: {
      calls: num(stats.totalCalls),
      tokens: num(stats.totalTokens),
    },
  }));

  const featureNodes = useMemo(() => {
    const entries = Object.entries(featureCounts || {});
    return entries
      .map(([feature, v]) => ({
        id: feature,
        label: feature,
        values: {
          calls: num(v.count),
          tokens: num(v.totalTokens),
          images: v.imageCount > 0 ? num(v.imageCount) : "—",
        },
        _calls: v.count ?? 0,
      }))
      .sort((a, b) => b._calls - a._calls);
  }, [featureCounts]);

  const monthlyPricingSlot = (
    <PlatformMonthlyPricing
      provider={provider}
      monthlyPrices={monthlyPrices}
      dateValue={dateValue}
      onSetMonthlyPrice={onSetMonthlyPrice}
    />
  );

  const views = [
    {
      key: "network",
      label: "By network",
      levels: LEVELS,
      nodes,
      emptyText: `No ${label} usage recorded for this period.`,
      searchPlaceholder: "Search networks…",
    },
  ];
  if (featureNodes.length) {
    const baseLevels = provider === "gemini" ? FEATURE_LEVELS_WITH_IMAGES : FEATURE_LEVELS;
    const featureLevelsWithProduct = [
      {
        ...baseLevels[0],
        columns: [
          {
            key: "product",
            header: "Product",
            align: "left",
            render: (node) => {
              const dbKey = `${provider}:${node.id}`;
              const saved = (productTags ?? []).find(
                (p) => p.keyType === "aiFeature" && p.key === dbKey,
              );
              return (
                <ProductInput
                  key={`${dbKey}-${saved?.product ?? ""}`}
                  defaultValue={saved?.product ?? ""}
                  onSave={(product) =>
                    onUpsertProduct?.({ id: saved?._id, keyType: "aiFeature", key: dbKey, product })
                  }
                />
              );
            },
          },
          ...baseLevels[0].columns,
        ],
      },
    ];
    views.push({
      key: "feature",
      label: "By feature",
      levels: featureLevelsWithProduct,
      nodes: featureNodes,
      emptyText: "No feature breakdown available.",
      searchPlaceholder: "Search features…",
      note: "Provider-wide totals across all networks.",
    });
  }

  return (
    <CostBreakdownModal
      isOpen={isOpen}
      onClose={onClose}
      title={`${label} — Usage`}
      subtitle="Network → agent → property → type"
      summary={[
        { label: "Networks", value: num(nodes.length) },
        { label: "Calls", value: num(totalCalls) },
        { label: "Tokens", value: num(totalTokens) },
      ]}
      headerSlot={monthlyPricingSlot}
      views={views}
      renderLazy={(node, depth) => (
        <NetworkAgents
          node={node}
          depth={depth}
          provider={provider}
          aiParams={aiParams}
        />
      )}
    />
  );
};

export default AiCostBreakdown;
